import { Download, FileUp, Loader2 } from "lucide-react";
import * as Papa from "papaparse";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useRef, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import { normalizarDominioWeb } from "../dominio/normalizacion";
import type { Negocio } from "../dominio/tipos";
import {
  COLUMNAS_CSV,
  convertirFila,
  mapearCabeceras,
  plantillaCSV,
  type ColumnaCSV,
  type FilaImportada,
} from "./csv";
import { leerTextoCSV } from "./lectura";

type Estado =
  | { paso: "inicio" }
  | {
      paso: "vista_previa";
      filas: FilaImportada[];
      omitidas: number;
      duplicadas: Set<number>;
      /** Filas cuya web (dominio normalizado) ya existe en otra ficha. */
      duplicadasWeb: Set<number>;
      mapa: Record<string, ColumnaCSV>;
      ignoradas: string[];
    }
  | { paso: "importando"; hechas: number; total: number }
  | { paso: "hecho"; ok: number; errores: number };

const TAMANO_LOTE = 10;

/** Importar negocios desde CSV (SPEC.md §8). Solo admins. */
/** Negocios que ya tienen alguno de esos teléfonos o dominios (consultas de 100 en 100). */
const negociosConSenales = async (
  dataProvider: NevoraDataProvider,
  telefonos: string[],
  dominios: string[],
): Promise<Negocio[]> => {
  const tandas = (xs: string[]) => {
    const unicos = [...new Set(xs)];
    return Array.from({ length: Math.ceil(unicos.length / 100) }, (_, i) =>
      unicos.slice(i * 100, (i + 1) * 100),
    );
  };
  const lista = (xs: string[]) =>
    `(${xs.map((x) => `"${x.replace(/"/g, "")}"`).join(",")})`;
  const filtros = [
    ...tandas(telefonos).map((t) => ({ "telefono_norm@in": lista(t) })),
    ...tandas(dominios).map((d) => ({ "dominio_web_norm@in": lista(d) })),
  ];
  const respuestas = await Promise.all(
    filtros.map((filter) =>
      dataProvider.getList<Negocio>("negocios", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
        filter,
      }),
    ),
  );
  return respuestas.flatMap((r) => r.data);
};

export const ImportarPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const esAdmin = useEsAdmin();
  const notify = useNotify();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [estado, setEstado] = useState<Estado>({ paso: "inicio" });

  const descargarPlantilla = () => {
    const blob = new Blob([String.fromCharCode(0xfeff) + plantillaCSV()], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla-negocios-nevora.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const leerFichero = async (fichero: File) => {
    // Codificación: Excel en español guarda Windows-1252; se intenta UTF-8 estricto y, si
    // falla, Windows-1252 (decisión 7 de Robert). Separador `;` o `,` autodetectado.
    let texto: string;
    try {
      texto = await leerTextoCSV(fichero);
    } catch {
      notify("nevora.importar.error_csv", { type: "error" });
      return;
    }
    const res = Papa.parse<Record<string, unknown>>(texto, {
      header: true,
      skipEmptyLines: true,
      delimiter: "",
    });
    if (res.errors.length && res.data.length === 0) {
      notify("nevora.importar.error_csv", { type: "error" });
      return;
    }
    const cabeceras = res.meta.fields ?? [];
    const { mapa, ignoradas } = mapearCabeceras(cabeceras);
    // Teléfonos y webs ya existentes, leídos en el momento de importar (no al abrir la
    // página) para que la detección de duplicados no dependa de una carga previa. Desde S10b
    // se piden a la base solo los del fichero (por tandas): con miles de negocios de
    // Prospect, leer «todos» se quedaba en los primeros 1.000.
    const convertidas = res.data.map((fila) => convertirFila(fila, mapa));
    const existentes = await negociosConSenales(
      dataProvider,
      convertidas.flatMap((f) => (f?.telefonoNorm ? [f.telefonoNorm] : [])),
      convertidas.flatMap((f) => {
        const d = f ? normalizarDominioWeb(f.negocio.web) : null;
        return d ? [d] : [];
      }),
    );
    const telefonosExistentes = new Map(
      existentes
        .filter((n) => n.telefono_norm)
        .map((n) => [n.telefono_norm as string, n.nombre]),
    );
    const dominiosExistentes = new Map<string, string>();
    for (const n of existentes) {
      const dominio = normalizarDominioWeb(n.web);
      if (dominio && !dominiosExistentes.has(dominio)) {
        dominiosExistentes.set(dominio, n.nombre);
      }
    }
    const filas: FilaImportada[] = [];
    const duplicadas = new Set<number>();
    const duplicadasWeb = new Set<number>();
    let omitidas = 0;
    for (const f of convertidas) {
      if (!f) {
        omitidas += 1;
        continue;
      }
      if (f.telefonoNorm && telefonosExistentes.has(f.telefonoNorm)) {
        duplicadas.add(filas.length);
      }
      const dominio = normalizarDominioWeb(f.negocio.web);
      if (dominio && dominiosExistentes.has(dominio)) {
        duplicadasWeb.add(filas.length);
      }
      filas.push(f);
    }
    setEstado({
      paso: "vista_previa",
      filas,
      omitidas,
      duplicadas,
      duplicadasWeb,
      mapa,
      ignoradas,
    });
  };

  const importar = async (filas: FilaImportada[]) => {
    let ok = 0;
    let errores = 0;
    setEstado({ paso: "importando", hechas: 0, total: filas.length });
    for (let i = 0; i < filas.length; i += TAMANO_LOTE) {
      const lote = filas.slice(i, i + TAMANO_LOTE);
      await Promise.all(
        lote.map(async (f) => {
          try {
            const { data } = await dataProvider.create<Negocio>("negocios", {
              data: f.negocio,
            });
            if (f.notas) {
              await dataProvider.create("notas", {
                data: {
                  negocio_id: data.id,
                  proyecto_id: data.proyecto_id,
                  canal: "otro",
                  texto: f.notas,
                  fecha: new Date().toISOString(),
                },
              });
            }
            ok += 1;
          } catch {
            errores += 1;
          }
        }),
      );
      setEstado({
        paso: "importando",
        hechas: Math.min(i + TAMANO_LOTE, filas.length),
        total: filas.length,
      });
    }
    setEstado({ paso: "hecho", ok, errores });
    notify("nevora.importar.hecho", {
      type: "success",
      messageArgs: { ok, errores },
    });
  };

  const cuerpo = !esAdmin ? (
    <p className="text-muted-foreground">
      {translate("nevora.importar.solo_admin")}
    </p>
  ) : (
    <div className="space-y-6 max-w-4xl pb-8">
      <p className="text-muted-foreground">
        {translate("nevora.importar.ayuda")}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={descargarPlantilla}>
          <Download className="size-4 mr-2" />
          {translate("nevora.importar.plantilla")}
        </Button>
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={estado.paso === "importando"}
        >
          <FileUp className="size-4 mr-2" />
          {translate("nevora.importar.elegir")}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void leerFichero(f);
            e.target.value = "";
          }}
        />
      </div>

      {estado.paso === "vista_previa" ? (
        <VistaPrevia
          estado={estado}
          onImportar={() => importar(estado.filas)}
        />
      ) : null}

      {estado.paso === "importando" ? (
        <p className="flex items-center gap-2">
          <Loader2 className="size-4 animate-spin" />
          {translate("nevora.importar.importando", {
            hechas: estado.hechas,
            total: estado.total,
          })}
        </p>
      ) : null}

      {estado.paso === "hecho" ? (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <p className="font-medium">
            {translate("nevora.importar.hecho", {
              ok: estado.ok,
              errores: estado.errores,
            })}
          </p>
          <div className="flex gap-2">
            <Button asChild>
              <Link to="/negocios">
                {translate("nevora.importar.ver_leads")}
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => setEstado({ paso: "inicio" })}
            >
              {translate("nevora.importar.otra")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="titulo-seccion">
            {translate("nevora.importar.titulo")}
          </h1>
        </MobileHeader>
        <MobileContent>{cuerpo}</MobileContent>
      </>
    );
  }
  return (
    <div className="mt-2 space-y-4">
      <h1 className="titulo-pagina">{translate("nevora.importar.titulo")}</h1>
      {cuerpo}
    </div>
  );
};

ImportarPage.path = "/importar";

const formatoCelda = (v: unknown) =>
  typeof v === "boolean" ? (v ? "Sí" : "No") : String(v ?? "");

const VistaPrevia = ({
  estado,
  onImportar,
}: {
  estado: Extract<Estado, { paso: "vista_previa" }>;
  onImportar: () => void;
}) => {
  const translate = useTranslate();
  const reconocidas = Object.values(estado.mapa);
  const columnasVisibles = COLUMNAS_CSV.filter(
    (c) => reconocidas.includes(c) && c !== "notas",
  ).slice(0, 6);
  const muestra = estado.filas.slice(0, 5);

  return (
    <div className="space-y-3">
      <p className="text-sm">
        {translate("nevora.importar.filas", {
          smart_count: estado.filas.length + estado.omitidas,
        })}
        {estado.omitidas > 0
          ? ` · ${estado.omitidas} ${translate("nevora.importar.sin_nombre").toLowerCase()}`
          : ""}
      </p>
      <p className="text-xs text-muted-foreground">
        {translate("nevora.importar.columnas", {
          columnas: reconocidas.join(", "),
        })}
        {estado.ignoradas.length
          ? ` · ${translate("nevora.importar.columnas_ignoradas", { columnas: estado.ignoradas.join(", ") })}`
          : ""}
      </p>
      {estado.duplicadas.size > 0 ? (
        <p className="text-sm text-orange-700 dark:text-orange-400">
          {translate("nevora.importar.duplicados_aviso", {
            smart_count: estado.duplicadas.size,
          })}
        </p>
      ) : null}
      {estado.duplicadasWeb.size > 0 ? (
        <p className="text-sm text-orange-700 dark:text-orange-400">
          {translate("nevora.importar.duplicados_web_aviso", {
            smart_count: estado.duplicadasWeb.size,
          })}
        </p>
      ) : null}
      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              {columnasVisibles.map((c) => (
                <th key={c} className="text-left px-3 py-2 font-medium">
                  {translate(`resources.negocios.fields.${c}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {muestra.map((f, i) => (
              <tr
                key={i}
                className={
                  estado.duplicadas.has(i) || estado.duplicadasWeb.has(i)
                    ? "bg-orange-50 dark:bg-orange-950/30"
                    : ""
                }
              >
                {columnasVisibles.map((c) => (
                  <td key={c} className="px-3 py-2 whitespace-nowrap">
                    {formatoCelda(f.negocio[c as keyof typeof f.negocio])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button onClick={onImportar} disabled={!estado.filas.length}>
        {translate("nevora.importar.importar", {
          smart_count: estado.filas.length,
        })}
      </Button>
    </div>
  );
};
